import type { ButtonProps } from './button.types'
/**
 * API DIFFERENCES vs ../button.tsx (shadcn):
 * - Variants: text|filled|outlined vs default|destructive|outline|secondary|ghost|link
 * - Size: small|default vs sm|default|lg|icon
 * - Extras: semanticRole (primary/secondary/success/error/warning/info)
 * - Extras: loading, gradient, toggled
 * - Children: validated (icon + label) vs any ReactNode
 * - For icon-only buttons, use IconButton component instead
 *
 * - Our text button is like ghost button in shadcn.
 * - Our filled button is like default button in shadcn.
 * - Our error button is like destructive button in shadcn.
 * - We don't have link button, because links can be anything (it doesn't make sense
 */
import { useClickAnimation } from '../hooks/use-click-animation'
import { cn } from '../lib/utils'
import {
  findIcon,
  extractTextContent,
  getButtonIconSize,
  processChildrenTree,
} from '../lib/children-utils'
import { LoadingContent } from '../lib/loading-content'
import { Button as BaseButton } from '@base-ui/react/button'
import { mergeProps } from '@base-ui/react/merge-props'
import { cva, type VariantProps } from 'class-variance-authority'
import flattenChildren from 'react-keyed-flatten-children'
import * as React from 'react'

const buttonVariants = cva(
  'focus-visible:ring-primary/80 inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-full px-6 py-2 transition-[background-color,color,border-color] duration-300 ease-out outline-none select-none focus-visible:ring-4 focus-visible:ring-offset-4 disabled:cursor-not-allowed [@media(hover:none)]:disabled:pointer-events-none',
  {
    variants: {
      variant: {
        // These 2 are empty because semantic roles heavily impact on them.
        text: '',
        filled: '',
        outlined: 'border-border bg-surface-2 border',
      },
      semanticRole: {
        // These are empty because the text/outlined/filled heavily impact on styles for the same semantic role.
        primary: '',
        secondary: '',
        success: '',
        error: '',
        warning: '',
        info: '',
      },
      size: {
        default: '',
        sm: 'h-6 px-4 py-0',
        lg: 'h-14 px-8 py-0',
      },
    },
    compoundVariants: [
      { variant: 'text', semanticRole: 'primary', className: 'text-primary' },
      {
        variant: 'outlined',
        semanticRole: 'primary',
        className: 'text-primary',
      },
      {
        variant: 'filled',
        semanticRole: 'primary',
        className: 'bg-primary text-on-primary',
      },
      {
        variant: 'text',
        semanticRole: 'secondary',
        className: 'text-secondary',
      },
      {
        variant: 'outlined',
        semanticRole: 'secondary',
        className: 'text-secondary bg-transparent',
      },
      {
        variant: 'filled',
        semanticRole: 'secondary',
        className: 'bg-secondary text-on-secondary',
      },
      { variant: 'text', semanticRole: 'success', className: 'text-success' },
      {
        variant: 'outlined',
        semanticRole: 'success',
        className: 'border-success-border bg-surface-success text-success',
      },
      {
        variant: 'filled',
        semanticRole: 'success',
        className: 'bg-success text-on-success',
      },
      { variant: 'text', semanticRole: 'error', className: 'text-error' },
      {
        variant: 'outlined',
        semanticRole: 'error',
        className: 'border-error-border bg-surface-error text-error',
      },
      {
        variant: 'filled',
        semanticRole: 'error',
        className: 'bg-error text-on-error',
      },
      { variant: 'text', semanticRole: 'warning', className: 'text-warning' },
      {
        variant: 'outlined',
        semanticRole: 'warning',
        className: 'border-warning-border bg-surface-warning text-warning',
      },
      {
        variant: 'filled',
        semanticRole: 'warning',
        className: 'bg-warning text-on-warning',
      },
      { variant: 'text', semanticRole: 'info', className: 'text-info' },
      {
        variant: 'outlined',
        semanticRole: 'info',
        className: 'border-info-border bg-surface-info text-info',
      },
      {
        variant: 'filled',
        semanticRole: 'info',
        className: 'bg-info text-on-info',
      },
    ],
    defaultVariants: {
      semanticRole: 'primary',
      variant: 'text',
      size: 'default',
    },
  },
)



/**
 * These 2 override the semantic role and variant styles:
 * @param gradient
 * @param toggled
 */
function Button({
  className,
  semanticRole,
  variant,
  size,
  loading,
  disabled,
  gradient,
  toggled,
  children,
  render,
  nativeButton,
  ...props
}: ButtonProps) {
  const clickAnimation = useClickAnimation()

  const [icon, labelText, originalChildren] =
    validateAndExtractChildren(children)

  const effectiveVariant = !icon && toggled !== undefined ? 'filled' : variant

  // Process children tree to apply icon transformations (size, fill)
  const processedChildren = processChildrenTree(originalChildren, {
    iconSize: getButtonIconSize({ size, variant: effectiveVariant }),
    iconFill:
      toggled === undefined ? undefined : toggled ? 'currentColor' : 'none',
  })

  return (
    <BaseButton
      className={cn(
        buttonVariants({
          semanticRole,
          variant: effectiveVariant,
          size,
        }),
        loading ? '!cursor-wait' : 'disabled:opacity-60',
        gradient && 'bg-gradient text-light-utility',
        toggled !== undefined && [
          effectiveVariant === 'filled' &&
            (toggled
              ? 'bg-primary text-on-primary'
              : 'bg-secondary text-on-secondary'),
          effectiveVariant === 'outlined' &&
            (toggled ? 'text-primary' : 'text-secondary'),
          effectiveVariant === 'text' &&
            (toggled ? 'text-primary' : 'text-secondary'),
        ],
        clickAnimation.className,
        className,
      )}
      data-slot="button"
      data-testid={labelText}
      // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: boolean || boolean, disabled can be false
      disabled={disabled || loading}
      render={render}
      nativeButton={
        nativeButton ?? !(React.isValidElement(render) && render.type === 'a')
      }
      {...mergeProps(clickAnimation.handlers, props)}
    >
      <LoadingContent loading={loading}>{processedChildren}</LoadingContent>
    </BaseButton>
  )
}

function validateAndExtractChildren(children: React.ReactNode) {
  const flattened = flattenChildren(children)

  if (flattened.length === 0)
    throw new Error('you forgot to pass children to Button.')

  if (flattened.length > 2) {
    throw new Error(
      `you passed too many children to Button - ${flattened.length}. Expected at most 2: an optional icon and a required label.`,
    )
  }

  // Traverse to find icon (can be nested)
  const icon = findIcon(children)
  // Extract text content (can be nested in spans, etc.)
  const labelText = extractTextContent(children)

  if (!labelText) {
    throw new Error(
      'Button must contain text content - you missed it. Text can be direct or wrapped in elements.',
    )
  }

  return [icon, labelText, children] as const
}

Button.renderable = true

export { Button, buttonVariants, type VariantProps }
